import {
  AppointmentModificationRequest,
  AppointmentOnlineCreationRequest,
  AppointmentsClient,
  CustomerRequest,
  NextServicePositionType,
  Package,
  PackagePosition,
  PackagePositionType,
  ServiceRequest,
  VehicleRequest,
  WorkshopCampaignDto
} from 'online-customer-services-ts'
import { useTranslator } from 'sima-suite-translator-vue3'

import {
  Appointment,
  AppointmentRequest,
  EditAppointmentRequest
} from '@/core/appointment/domain/Appointment.ts'
import { AppointmentRepository } from '@/core/appointment/domain/AppointmentRepository.ts'
import { createWorkshopDate } from '@/utils'
import dayjs from '@/utils/dayjs.ts'

const APPOINTMENT_SOURCE = 'OnlineCustomerService'

export class AppointmentRepositoryBackendClient implements AppointmentRepository {
  private readonly appointmentClient: AppointmentsClient

  constructor(url: string) {
    this.appointmentClient = new AppointmentsClient(url)
  }

  async cancelAppointment(
    hostId: string,
    enterpriseId: string,
    appointmentId: string
  ): Promise<void> {
    return await this.appointmentClient.cancelAppointment(enterpriseId, appointmentId, hostId)
  }

  async createAppointment(request: AppointmentRequest): Promise<void> {
    const {
      adviser,
      availability,
      campaigns,
      customer,
      date,
      hostId,
      packages,
      services,
      vehicle,
      workshop
    } = request
    const { language } = useTranslator()

    return await this.appointmentClient.createAppointment22(
      hostId,
      workshop.simaId.toString(),
      new AppointmentOnlineCreationRequest({
        adviserId: Number(adviser.id),
        campaigns: campaigns.map(
          (campaign) =>
            new WorkshopCampaignDto({
              campaignCode: campaign.code,
              campaignName: campaign.name,
              necessaryHours: campaign.hours
            })
        ),
        cultureName: language.value,
        customer: new CustomerRequest({
          customerId: undefined,
          email: customer.email,
          mobile: customer.mobilePhone,
          name: customer.name,
          phone: customer.phone
        }),
        packages: packages.map(
          (ppsoPackage) =>
            new Package({
              denomination: ppsoPackage.denomination,
              id: ppsoPackage.id,
              number: ppsoPackage.packageNumber,
              positions: ppsoPackage.positions.map(
                (position) =>
                  new PackagePosition({
                    code: position.number,
                    denomination: position.denomination,
                    mark: position.mark,
                    quantity: position.quantity,
                    type:
                      position.positionType === NextServicePositionType.Labour
                        ? PackagePositionType.Labor
                        : PackagePositionType.Part
                  })
              ),
              udt: ppsoPackage.udt
            })
        ),
        services: services.map(
          (service) =>
            new ServiceRequest({
              id: Number(service.id),
              remarks: service.remarks
            })
        ),
        source: APPOINTMENT_SOURCE,
        teamId: availability[date].teamId,
        vehicle: new VehicleRequest({
          brandCode: vehicle.brandCode,
          kms: vehicle.kilometers,
          model: vehicle.model,
          plate: vehicle.plate,
          vin: vehicle.vin ?? undefined
        }),
        when: createWorkshopDate(date, adviser.time),
        workshopId: workshop.id
      })
    )
  }

  async editAppointment(request: EditAppointmentRequest): Promise<void> {
    const { adviser, appointmentId, date, enterpriseId, hostId, teamId, workshop } = request

    const { language } = useTranslator()

    const requestData = new AppointmentModificationRequest({
      adviserId: Number(adviser.id),
      cultureName: language.value,
      date: createWorkshopDate(date, adviser.time),
      teamId,
      workshopId: workshop.id
    })

    return await this.appointmentClient.modifyAppointment(
      enterpriseId,
      appointmentId,
      hostId,
      requestData
    )
  }

  async getAppointment(hostId: string, enterpriseId: string, id: string): Promise<Appointment> {
    const appointment = await this.appointmentClient.get(hostId, enterpriseId, id)

    // Convert appointment date/time from UTC to workshop timezone
    let date = ''
    let time = ''

    if (appointment.appointmentDateTime) {
      const workshopTimeZone = appointment.workshop?.timeZoneId ?? 'UTC'
      const dateTime = appointment.appointmentDateTime?.split(' ') ?? []
      const dateTemp = new Date(`${dateTime[0]}T${dateTime[1]}:00.000Z`)
      const appointmentInWorkshopTz = dayjs.utc(dateTemp).tz(workshopTimeZone)
      date = appointmentInWorkshopTz.format('YYYY-MM-DD')
      time = appointmentInWorkshopTz.format('HH:mm')
    }

    return {
      adviser: { name: appointment.adviserName ?? '' },
      customer: {
        email: appointment.customer?.email ?? '',
        mobilePhone: appointment.customer?.mobile ?? '',
        name: appointment.customer?.name ?? '',
        phone: appointment.customer?.phone ?? ''
      },
      date: date,
      id: appointment.appointmentId ?? '',
      servicesTotalUdts: appointment.servicesTotalUdts ?? NaN,
      teamId: appointment.teamId ?? NaN,
      time: time,
      vehicle: {
        brand: appointment.vehicle?.brand ?? '',
        brandCode: appointment.vehicle?.brandCode ?? '',
        kilometers: appointment.vehicle?.kms ?? NaN,
        model: appointment.vehicle?.model ?? '',
        plate: appointment.vehicle?.plate ?? '',
        vin: appointment.vehicle?.vin ?? ''
      },
      workshop: {
        addressPart1: appointment.workshop?.addressPart1 ?? '',
        addressPart2: appointment.workshop?.addressPart2 ?? '',
        brands:
          appointment.workshop?.brands?.map((brand) => {
            return {
              code: brand.code ?? '',
              serviceId: brand.serviceId ?? NaN
            }
          }) ?? [],
        daysOfMarginToRequest: appointment.workshop?.daysOfMarginToRequest ?? NaN,
        endingWorkHour: appointment.workshop?.endingWorkHour ?? '',
        id: appointment.workshop?.id ?? NaN,
        idInEnterprise: appointment.workshop?.idInEnterprise ?? NaN,
        latitude: appointment.workshop?.latitude ?? NaN,
        longitude: appointment.workshop?.longitude ?? NaN,
        name: appointment.workshop?.name ?? '',
        noWorkingWeekDays: appointment.workshop?.noWorkingWeekDays ?? [],
        phone: appointment.workshop?.phone ?? '',
        redirectUrl: appointment.workshop?.redirectUrl ?? '',
        schedulerTimeSlot: appointment.workshop?.schedulerTimeSlot ?? NaN,
        simaId: appointment.workshop?.simaId ?? NaN,
        startingWorkHour: appointment.workshop?.startingWorkHour ?? '',
        timeZoneId: appointment.workshop?.timeZoneId ?? ''
      }
    }
  }
}
